import { forwardRef, useImperativeHandle, useState } from "react";

import "./stylesProfitBoostModal.css";

import { CloseOutlined } from "@ant-design/icons";

import { AppStrings } from "../../../../../utils/constants/AppStrings";
import { API_ROUTES } from "../../../../../api/ApiRoutes";
import createPrivateAdminPanelInstance from "../../../../../api/PrivateAdminPanelInstance";
import RebetLoader from "../../../../CommonComponents/RebetLoader/RebetLoader";
import { Button, Form, Input, message } from "antd";

const requiredRule = (fieldLabel) => ({
  required: true,
  message: AppStrings.FIELD_IS_REQUIRED.replace("{title}", fieldLabel),
});

const MIN_PARLAY_LEGS_MIN = 2;

const minParlayLegsRules = [
  requiredRule(AppStrings.MINIMUM_PARLEY_LEGS),
  {
    validator: (_, val) => {
      const n = parseInt(val, 10);
      if (Number.isNaN(n) || n < MIN_PARLAY_LEGS_MIN) {
        return Promise.reject(
          new Error(AppStrings.MINIMUM_PARLEY_LEGS_MIN_VALIDATION),
        );
      }
      return Promise.resolve();
    },
  },
];

const ProfitBoostModal = forwardRef(({ handleCancel, data }, ref) => {
  const token = JSON.parse(localStorage.getItem("token"));
  const PrivateAdminPanelInstance = createPrivateAdminPanelInstance(
    token?.AccessToken,
  );

  const [form] = Form.useForm();
  const [isApiRunning, setIsApiRunning] = useState(false);

  const clearForm = () => {
    form.resetFields();
    setIsApiRunning(false);
  };

  const handleFinish = async (values) => {
    if (!data?.user_id) return;

    setIsApiRunning(true);

    const params = {
      user_id: data.user_id,
      percentage: values.percentage,
      exp_time: values.expiry_hours,
      exp_time_format: "hours",
      min_parlay_legs: values.min_parlay_legs,
      reason: (values.reason || "").trim(),
    };

    try {
      await PrivateAdminPanelInstance?.post(
        API_ROUTES?.GIVE_USER_PROFIT_BOOST?.replace(
          "{user_id}",
          params?.user_id,
        ),
        params,
      );

      message.success(AppStrings.PROFIT_BOOST_ADDED_SUCCESSFULLY);
      clearForm();
    } catch (error) {
      console.error("Error in setting Profit boost:", error);
      message.error(AppStrings.ERROR_PROCESSING_REQUEST);
    } finally {
      setIsApiRunning(false);
    }
  };

  useImperativeHandle(ref, () => ({
    clearForm,
  }));

  return (
    <div className="profit-boost-modal-container">
      <div className="modal-header">
        <div className="modal-header-title">
          <h2 className="modal-title"> {AppStrings.PROFIT_BOOST}</h2>
        </div>
        <CloseOutlined onClick={handleCancel} className="close-icon" />
      </div>

      <div className="profit-boost-body">
        <Form
          form={form}
          layout="vertical"
          className="profit-boost-form"
          onFinish={handleFinish}
          requiredMark={false}
          initialValues={{
            percentage: undefined,
            expiry_hours: undefined,
            min_parlay_legs: undefined,
            reason: "",
          }}
        >
          <Form.Item
            className="form-group"
            label={`${AppStrings.PERCENTAGE}:`}
            name="percentage"
            rules={[requiredRule(AppStrings.PERCENTAGE)]}
          >
            <Input
              type="number"
              min={0}
              name="percentage"
              placeholder="%"
              onWheel={(e) => e.target.blur()}
            />
          </Form.Item>
          <Form.Item
            className="form-group"
            label={`${AppStrings.EXPIRY_HOURS}:`}
            name="expiry_hours"
            rules={[requiredRule(AppStrings.EXPIRY_HOURS)]}
          >
            <Input
              type="number"
              min={0}
              name="expiry_hours"
              placeholder="# of hrs"
              onWheel={(e) => e.target.blur()}
            />
          </Form.Item>
          <Form.Item
            className="form-group"
            label={`${AppStrings.MINIMUM_PARLEY_LEGS}:`}
            name="min_parlay_legs"
            rules={minParlayLegsRules}
          >
            <Input
              type="number"
              min={MIN_PARLAY_LEGS_MIN}
              name="min_parlay_legs"
              placeholder="#"
              onWheel={(e) => e.target.blur()}
            />
          </Form.Item>
          <Form.Item
            className="form-group"
            label={`${AppStrings.REASON}:`}
            name="reason"
            rules={[
              requiredRule(AppStrings.REASON),
              { whitespace: true, message: AppStrings.FIELD_IS_REQUIRED.replace("{title}", AppStrings.REASON) },
            ]}
          >
            <Input.TextArea
              rows={4}
              name="reason"
              placeholder={AppStrings.ENTER_REASON_PLACEHOLDER}
            />
          </Form.Item>
          <Form.Item className="profit-boost-form-actions">
            <Button
              type="primary"
              htmlType="submit"
              className={`profit-boost-submit-btn ${isApiRunning ? "button-sending" : ""}`}
              disabled={isApiRunning}
            >
              {isApiRunning ? (
                <RebetLoader height={"50px"} width={"50px"} />
              ) : (
                AppStrings.ADD_PROFIT_BOOST
              )}
            </Button>
          </Form.Item>
        </Form>
      </div>
    </div>
  );
});

export default ProfitBoostModal;
